import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PageLayout } from '../../components/DesignSystem';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';
import { LIST_PAGE_SIZE, ListPagination } from '../../components/ListPagination';
import '../../styles/listPage.css';

const TYPE_CONFIG: Record<string, { label: string; color: string; sign: string }> = {
  // New simplified types
  stock_in:        { label: 'Stock In',           color: '#10b981', sign: '+' },
  stock_out:       { label: 'Stock Out',           color: '#ef4444', sign: '-' },
  adjustment:      { label: 'Adjustment',          color: '#f59e0b', sign: '±' },
  // Legacy types (for historical records)
  sale:                    { label: 'Sale',                  color: '#ef4444', sign: '-' },
  receipt:                 { label: 'Receipt (old)',          color: '#10b981', sign: '+' },
  purchase_order:          { label: 'PO Receipt',            color: '#3b82f6', sign: '+' },
  purchase_order_cancel:   { label: 'PO Reversal',           color: '#dc2626', sign: '-' },
  order_cancel:            { label: 'Order Reversal',        color: '#dc2626', sign: '+' },
  return:                  { label: 'Customer Return (old)', color: '#8b5cf6', sign: '+' },
  transfer:                { label: 'Transfer (old)',         color: '#6b7280', sign: '±' },
  supplier_return:         { label: 'Supplier Return',       color: '#f97316', sign: '-' },
  damage:                  { label: 'Damage / Write-off',    color: '#9ca3af', sign: '-' },
  reversal:                { label: 'Reversal',              color: '#9333ea', sign: '±' },
};

const StockMovements: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [movements, setMovements] = useState<any[]>([]);
  const [movementsTotal, setMovementsTotal] = useState(0);
  const [movementsPage, setMovementsPage] = useState(1);
  const MOVEMENTS_LIMIT = LIST_PAGE_SIZE;
  const movementsPages = Math.ceil(movementsTotal / MOVEMENTS_LIMIT) || 1;
  const [products, setProducts] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [summary, setSummary] = useState({ movement_count: 0, products_affected: 0, units_in: 0, units_out: 0, value_in: 0, value_out: 0 });
  const [loading, setLoading] = useState(true);
  const [activeMovement, setActiveMovement] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [productFilter, setProductFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [dateFilter, setDateFilter] = useState<'All' | 'Today' | '7d' | '30d'>('30d');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // CSV import state
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvRows, setCsvRows] = useState<any[]>([]);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState<Array<{ row: number; error: string }>>([]);
  const [form, setForm] = useState({
    product_id: '',
    type: 'stock_in',
    quantity: '',
    unit_cost: '',
    reason: '',
    supplier_id: '',
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setMovementsPage(1);
  }, [typeFilter, productFilter, supplierFilter, dateFilter, debouncedSearch]);

  const getDateRange = () => {
    if (dateFilter === 'All') return {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    if (dateFilter === 'Today') return { date_from: fmt(today), date_to: fmt(today) };
    const start = new Date(today);
    start.setDate(start.getDate() - (dateFilter === '7d' ? 7 : 30));
    return { date_from: fmt(start), date_to: fmt(today) };
  };

  const buildQueryString = () => {
    const params = new URLSearchParams();
    params.set('page', String(movementsPage));
    params.set('limit', String(MOVEMENTS_LIMIT));
    if (typeFilter !== 'All') params.set('type', typeFilter);
    if (productFilter) params.set('product_id', productFilter);
    if (supplierFilter) params.set('supplier_id', supplierFilter);
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    const dates = getDateRange();
    if (dates.date_from) params.set('date_from', dates.date_from);
    if (dates.date_to) params.set('date_to', dates.date_to);
    return params.toString();
  };

  useEffect(() => {
    fetchAll();
  }, [token, movementsPage, typeFilter, productFilter, supplierFilter, dateFilter, debouncedSearch]);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      apiFetch(`${API_URL}/products/dropdown`, { headers: { Authorization: `Bearer ${token}` } }),
      apiFetch(`${API_URL}/suppliers`, { headers: { Authorization: `Bearer ${token}` } }),
    ]).then(([prodRes, suppRes]) => {
      if (prodRes.ok) prodRes.json().then(setProducts);
      if (suppRes.ok) {
        suppRes.json().then((sd) => setSuppliers(Array.isArray(sd) ? sd : sd.suppliers || []));
      }
    }).catch(console.error);
  }, [token]);

  const fetchAll = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const qs = buildQueryString();
      const summaryQs = qs.replace(/page=\d+&?/, '').replace(/limit=\d+&?/, '');
      const [movRes, summaryRes] = await Promise.all([
        apiFetch(`${API_URL}/stock-movements?${qs}`, { headers: { Authorization: `Bearer ${token}` } }),
        apiFetch(`${API_URL}/stock-movements/summary?${summaryQs}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (movRes.ok) {
        const movData = await movRes.json();
        if (Array.isArray(movData)) { setMovements(movData); setMovementsTotal(movData.length); }
        else { setMovements(movData.data || []); setMovementsTotal(movData.total || 0); }
      }
      if (summaryRes.ok) setSummary(await summaryRes.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // When a product is selected, auto-detect supplier by ID (preferred) or legacy name match
  const handleProductChange = (productId: string) => {
    const product = products.find((p: any) => String(p.id) === productId);
    let autoSupplierId = '';
    if (product?.supplier) {
      const supVal = String(product.supplier).trim();
      const byId = suppliers.find((sup: any) => String(sup.id) === supVal);
      const byName = suppliers.find((sup: any) =>
        sup.name.toLowerCase().trim() === supVal.toLowerCase()
      );
      if (byId) autoSupplierId = String(byId.id);
      else if (byName) autoSupplierId = String(byName.id);
    }
    if (!autoSupplierId && product?.supplier_id) {
      autoSupplierId = String(product.supplier_id);
    }
    const cost = product?.last_purchase_price ?? product?.avg_cost ?? product?.cost;
    setForm(f => ({
      ...f,
      product_id: productId,
      supplier_id: autoSupplierId,
      unit_cost: cost != null && cost !== '' ? String(cost) : f.unit_cost,
    }));
  };

  const isPoReference = (ref?: string) => !!ref && /^PO-\d{4}-\d+/i.test(ref);

  const isOrderReference = (ref?: string, type?: string) =>
    !!ref && (type === 'sale' || type === 'order_cancel' || /^ORD-/i.test(ref) || /^\d+$/.test(ref));

  const movementLineValue = (m: any) => {
    const v = parseFloat(m.line_value);
    if (!isNaN(v) && v > 0) return v;
    const cost = parseFloat(m.unit_cost) || 0;
    return Math.abs(parseFloat(m.quantity) || 0) * cost;
  };

  const openDetail = async (movement: any) => {
    setShowDetail(true);
    setDetailLoading(true);
    setActiveMovement(movement);
    try {
      const res = await apiFetch(`${API_URL}/stock-movements/${movement.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setActiveMovement(await res.json());
    } catch {
      showToast('Failed to load movement detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const submitReverse = async () => {
    if (!activeMovement?.can_reverse) return;
    if (!window.confirm('Reverse this movement? Stock and accounting entries will be undone.')) return;

    setSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/stock-movements/${activeMovement.id}/reverse`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Manual reversal from Stock Movements' }),
      });
      if (res.ok) {
        setShowDetail(false);
        setActiveMovement(null);
        await fetchAll();
        setToast('Movement reversed successfully');
        setTimeout(() => setToast(null), 3500);
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to reverse movement');
      }
    } catch {
      showToast('Network error');
    } finally {
      setSaving(false);
    }
  };

  const navigateToSource = (m: any) => {
    if (m.linked_po_id || isPoReference(m.reference)) {
      navigate(`/purchase-orders?q=${encodeURIComponent(m.reference || '')}`);
      return;
    }
    if (m.linked_order_id) {
      navigate(`/orders/view/${m.linked_order_id}`);
      return;
    }
    if (isOrderReference(m.reference, m.type)) {
      navigate(`/orders/list?q=${encodeURIComponent(m.reference || '')}`);
    }
  };

  const lowStockProducts = products.filter((p: any) => {
    const min = parseFloat(p.min_stock ?? p.minStock ?? 5);
    return parseFloat(p.stock ?? 0) <= min;
  });

  const exportCsv = () => {
    if (!movements.length) { showToast('No movements to export'); return; }
    const headers = ['date', 'time', 'product', 'type', 'supplier', 'before', 'change', 'after', 'unit_cost', 'value', 'reference', 'note', 'by'];
    const lines = movements.map((m: any) => [
      new Date(m.created_at).toLocaleDateString('en-GB'),
      new Date(m.created_at).toLocaleTimeString('en-GB'),
      m.product_name,
      m.type,
      m.supplier_name || '',
      m.before_quantity ?? '',
      m.quantity,
      m.after_quantity ?? '',
      m.unit_cost ?? '',
      movementLineValue(m).toFixed(2),
      m.reference || '',
      m.note || '',
      m.created_by_name || '',
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-movements-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const text = (ev.target?.result as string) || '';
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { showToast('CSV must have a header row + at least one data row'); return; }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      const rows = lines.slice(1).map((line, i) => {
        const cols = line.split(',').map(c => c.trim());
        const obj: Record<string, string> = {};
        headers.forEach((h, idx) => { obj[h] = cols[idx] || ''; });
        return obj;
      });
      setCsvRows(rows);
      setImportErrors([]);
      setShowImportModal(true);
    };
    reader.readAsText(file);
    // reset so same file can be re-selected
    e.target.value = '';
  };

  const submitCsvImport = async () => {
    setImporting(true);
    setImportErrors([]);
    try {
      const res = await apiFetch(`${API_URL}/stock-movements/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: csvRows }),
      });
      const data = res ? await res.json() : null;
      if (res?.ok) {
        setShowImportModal(false);
        setCsvRows([]);
        await fetchAll();
        setToast(`Imported ${data.imported} movement${data.imported !== 1 ? 's' : ''} successfully!`);
        setTimeout(() => setToast(null), 3500);
      } else {
        setImportErrors(data?.errors || [{ row: 0, error: data?.error || 'Import failed' }]);
      }
    } catch {
      setImportErrors([{ row: 0, error: 'Network error' }]);
    } finally {
      setImporting(false);
    }
  };

  const submitMovement = async () => {
    if (!form.product_id || !form.quantity || parseFloat(form.quantity) === 0) {
      showToast('Select a product and enter a non-zero quantity');
      return;
    }
    if (form.type === 'stock_out' && !form.reason) {
      showToast('Select a reason for Stock Out');
      return;
    }
    if (form.type === 'stock_out' && form.reason === 'Supplier return' && !form.supplier_id) {
      showToast('Select a supplier for Supplier return');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/stock-movements`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: parseInt(form.product_id),
          type: form.type,
          quantity: parseFloat(form.quantity),
          unit_cost: form.unit_cost ? parseFloat(form.unit_cost) : null,
          supplier_id: form.supplier_id ? parseInt(form.supplier_id) : null,
          reference: form.reason || null,
          note: form.reason || null,
        }),
      });
      if (res.ok) {
        setShowModal(false);
        setForm({ product_id: '', type: 'stock_in', quantity: '', unit_cost: '', reason: '', supplier_id: '' });
        await fetchAll();
        setToast('Movement saved successfully!');
        setTimeout(() => setToast(null), 3500);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to record movement');
      }
    } catch (e) {
      showToast('Network error');
    } finally {
      setSaving(false);
    }
  };

  const filtered = movements;

  const totalIn = Number(summary.units_in || 0);
  const totalOut = Number(summary.units_out || 0);

  const s: Record<string, React.CSSProperties> = {
    card: { background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 16 },
    badge: { padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: '#fff', display: 'inline-block' },
    th: { padding: '10px 14px', fontWeight: 600, fontSize: 13, color: '#374151', textAlign: 'left' as const, borderBottom: '1px solid #e5e7eb', background: '#f9fafb' },
    td: { padding: '10px 14px', fontSize: 14, borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' as const },
    input: { width: '100%', padding: '8px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const },
    overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
    modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, padding: 32 },
    label: { fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6, color: '#374151' },
    formGroup: { marginBottom: 16 },
  };

  return (
    <PageLayout
      title="📦 Stock Movements"
      action={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input ref={csvInputRef} type="file" accept=".csv" onChange={handleCsvFile} style={{ display: 'none' }} />
          <button
            onClick={exportCsv}
            style={{ padding: '10px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            📤 Export CSV
          </button>
          <button
            onClick={() => csvInputRef.current?.click()}
            style={{ padding: '10px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            📥 Import CSV
          </button>
          <button
            onClick={() => setShowModal(true)}
            style={{ padding: '10px 20px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            ➕ Manual Movement
          </button>
        </div>
      }
    >
      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: '#10b981', color: '#fff', padding: '14px 24px',
          borderRadius: 10, fontWeight: 600, fontSize: 15,
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          ✅ {toast}
        </div>
      )}

      {/* Workflow guide */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        {[
          { label: 'PO Receive', desc: 'Auto from Purchase Orders', color: '#3b82f6' },
          { label: 'Manual In/Out', desc: 'Deliveries, damage, returns', color: '#10b981' },
          { label: 'Adjustments', desc: 'Stock count corrections', color: '#f59e0b' },
          { label: 'Accounting', desc: 'In/out creates ledger entries', color: '#6366f1' },
        ].map((step, i) => (
          <div key={i} style={{ flex: 1, padding: '10px 14px', borderRight: i < 3 ? '1px solid #e2e8f0' : 'none' }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: step.color }}>{step.label}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>{step.desc}</div>
          </div>
        ))}
      </div>

      {lowStockProducts.length > 0 && (
        <div style={{ ...s.card, background: '#fffbeb', border: '1px solid #fde68a', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, color: '#92400e' }}>⚠️ {lowStockProducts.length} product{lowStockProducts.length !== 1 ? 's' : ''} at or below reorder point</div>
            <div style={{ fontSize: 13, color: '#b45309' }}>Review stock levels and create purchase orders to restock.</div>
          </div>
          <button
            onClick={() => navigate('/purchase-orders')}
            style={{ padding: '8px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            📋 Open Purchase Orders
          </button>
        </div>
      )}

      {/* Summary Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ ...s.card, flex: 1, minWidth: 140, padding: '14px 20px', textAlign: 'center', marginBottom: 0 }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>+{totalIn.toFixed(0)}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Units In</div>
        </div>
        <div style={{ ...s.card, flex: 1, minWidth: 140, padding: '14px 20px', textAlign: 'center', marginBottom: 0 }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444' }}>{totalOut.toFixed(0)}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Units Out</div>
        </div>
        <div style={{ ...s.card, flex: 1, minWidth: 140, padding: '14px 20px', textAlign: 'center', marginBottom: 0 }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#3b82f6' }}>{summary.movement_count || movementsTotal}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Movements {dateFilter !== 'All' ? `(${dateFilter === 'Today' ? 'today' : dateFilter})` : ''}</div>
        </div>
        <div style={{ ...s.card, flex: 1, minWidth: 140, padding: '14px 20px', textAlign: 'center', marginBottom: 0 }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#6b7280' }}>{summary.products_affected || 0}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Products Affected</div>
        </div>
        <div style={{ ...s.card, flex: 1, minWidth: 160, padding: '14px 20px', textAlign: 'center', marginBottom: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>£{Number(summary.value_in || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Value In</div>
        </div>
        <div style={{ ...s.card, flex: 1, minWidth: 160, padding: '14px 20px', textAlign: 'center', marginBottom: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#ef4444' }}>£{Number(summary.value_out || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Value Out</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ ...s.input, width: 260, padding: '6px 12px' }}
          placeholder="Search product, reference, note, user..."
        />
        <div>
          <label style={{ fontSize: 13, fontWeight: 500, marginRight: 6, color: '#374151' }}>Type:</label>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
            <option value="All">All</option>
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 500, marginRight: 6, color: '#374151' }}>Product:</label>
          <select value={productFilter} onChange={e => setProductFilter(e.target.value)}
            style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
            <option value="">All Products</option>
            {products.map((p: any) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 500, marginRight: 6, color: '#374151' }}>Supplier:</label>
          <select value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)}
            style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
            <option value="">All Suppliers</option>
            {suppliers.map((sup: any) => <option key={sup.id} value={String(sup.id)}>{sup.name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 500, marginRight: 6, color: '#374151' }}>Date:</label>
          <select value={dateFilter} onChange={e => setDateFilter(e.target.value as any)}
            style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
            <option value="All">All Time</option>
            <option value="Today">Today</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
        {(typeFilter !== 'All' || productFilter || supplierFilter || dateFilter !== '30d' || searchTerm) && (
          <button onClick={() => { setTypeFilter('All'); setProductFilter(''); setSupplierFilter(''); setDateFilter('30d'); setSearchTerm(''); }}
            style={{ fontSize: 13, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>✕ Clear</button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>Loading...</div>
      ) : (
        <>
        <ListPagination
          total={movementsTotal}
          filtered={movementsTotal}
          page={movementsPage}
          pages={movementsPages}
          onPageChange={setMovementsPage}
          noun="movements"
        />
        <div className="list-table-panel">
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Date & Time', 'Product', 'Type', 'Supplier', 'Before', 'Change', 'After', 'Value', 'Source', 'Note', 'By', ''].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
                  No stock movements match your filters. Movements are recorded automatically from orders and PO receipts, or add manual adjustments.
                </td></tr>
              ) : filtered.map(m => {
                const cfg = TYPE_CONFIG[m.type] || { label: m.type, color: '#6b7280', sign: '±' };
                const qty = parseFloat(m.quantity);
                const isPositive = qty > 0;
                const beforeQty = m.before_quantity !== null && m.before_quantity !== undefined ? parseFloat(m.before_quantity) : null;
                const afterQty = m.after_quantity !== null && m.after_quantity !== undefined ? parseFloat(m.after_quantity) : null;
                const lineValue = movementLineValue(m);
                const isReversed = !!m.reversed_by_id || !!m.reversal_id;
                return (
                  <tr key={m.id} style={{ background: isReversed ? '#fafafa' : '#fff', cursor: 'pointer' }}
                    onClick={() => openDetail(m)}>
                    <td style={s.td}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{new Date(m.created_at).toLocaleDateString('en-GB')}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(m.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                    <td style={s.td}><strong>{m.product_name}</strong></td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: cfg.color }}>{cfg.label}</span>
                      {m.reversed_by_id && <div style={{ fontSize: 10, color: '#9333ea', marginTop: 4, fontWeight: 600 }}>Reversed</div>}
                      {m.reversal_of_id && <div style={{ fontSize: 10, color: '#9333ea', marginTop: 4, fontWeight: 600 }}>Reversal entry</div>}
                    </td>
                    <td style={s.td}>{m.supplier_name ? <span style={{ fontSize: 12, color: '#4b5563' }}>🏭 {m.supplier_name}</span> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td style={s.td}>{beforeQty !== null ? <strong>{beforeQty.toFixed(0)}</strong> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td style={s.td}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: isPositive ? '#10b981' : '#ef4444' }}>
                        {isPositive ? '+' : ''}{qty.toFixed(0)}
                      </span>
                    </td>
                    <td style={s.td}>{afterQty !== null ? <strong>{afterQty.toFixed(0)}</strong> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td style={s.td}>
                      {lineValue > 0 ? (
                        <span style={{ fontWeight: 600, color: isPositive ? '#10b981' : '#ef4444' }}>
                          £{lineValue.toFixed(2)}
                        </span>
                      ) : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={s.td} onClick={e => e.stopPropagation()}>
                      {m.reference ? (
                        (isPoReference(m.reference) || m.type === 'purchase_order' || m.type === 'sale' || m.type === 'order_cancel') ? (
                          <button
                            onClick={() => navigateToSource(m)}
                            style={{ background: 'none', border: 'none', padding: 0, color: '#3b82f6', cursor: 'pointer', fontWeight: 600, fontSize: 13, textDecoration: 'underline' }}
                          >
                            {m.reference}
                          </button>
                        ) : (
                          m.reference
                        )
                      ) : (
                        <span style={{ color: '#9ca3af' }}>Manual</span>
                      )}
                    </td>
                    <td style={s.td}>{m.note || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td style={s.td}>{m.created_by_name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td style={s.td} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => openDetail(m)}
                        style={{ padding: '4px 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>

        <div className="list-mobile-cards">
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
              No stock movements match your filters.
            </div>
          ) : filtered.map(m => {
            const cfg = TYPE_CONFIG[m.type] || { label: m.type, color: '#6b7280', sign: '±' };
            const qty = parseFloat(m.quantity);
            const isPositive = qty > 0;
            return (
              <div key={m.id} className="list-mobile-card" onClick={() => openDetail(m)} style={{ cursor: 'pointer' }}>
                <div className="list-mobile-card-header">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="list-mobile-card-title">{m.product_name}</div>
                    <div className="list-mobile-card-meta">{new Date(m.created_at).toLocaleString('en-GB')}</div>
                  </div>
                  <span style={{ ...s.badge, background: cfg.color }}>{cfg.label}</span>
                </div>
                <div className="list-mobile-card-info">
                  <div><div className="list-mobile-card-label">Change</div><span style={{ fontWeight: 700, color: isPositive ? '#10b981' : '#ef4444' }}>{isPositive ? '+' : ''}{qty.toFixed(0)}</span></div>
                  <div><div className="list-mobile-card-label">Value</div>£{movementLineValue(m).toFixed(2)}</div>
                  <div><div className="list-mobile-card-label">Supplier</div>{m.supplier_name || '—'}</div>
                  <div><div className="list-mobile-card-label">Source</div>{m.reference || 'Manual'}</div>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* Movement Detail Modal */}
      {showDetail && activeMovement && (
        <div style={s.overlay} onClick={() => { setShowDetail(false); setActiveMovement(null); }}>
          <div style={{ ...s.modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>Movement #{activeMovement.id}</h2>

            {detailLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading...</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16, padding: 14, background: '#f9fafb', borderRadius: 10 }}>
                  <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>PRODUCT</div><div style={{ fontWeight: 600 }}>{activeMovement.product_name}</div></div>
                  <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>TYPE</div><div>{TYPE_CONFIG[activeMovement.type]?.label || activeMovement.type}</div></div>
                  <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>QUANTITY</div><div style={{ fontWeight: 700, color: parseFloat(activeMovement.quantity) > 0 ? '#10b981' : '#ef4444' }}>{parseFloat(activeMovement.quantity) > 0 ? '+' : ''}{parseFloat(activeMovement.quantity)}</div></div>
                  <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>STOCK</div><div>{parseFloat(activeMovement.before_quantity ?? 0)} → <strong>{parseFloat(activeMovement.after_quantity ?? 0)}</strong> (now {activeMovement.current_stock ?? '—'})</div></div>
                  <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>UNIT COST</div><div>{activeMovement.unit_cost != null ? `£${parseFloat(activeMovement.unit_cost).toFixed(2)}` : '—'}</div></div>
                  <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>LINE VALUE</div><div style={{ fontWeight: 700 }}>£{movementLineValue(activeMovement).toFixed(2)}</div></div>
                  <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>SUPPLIER</div><div>{activeMovement.supplier_name || '—'}</div></div>
                  <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>BY</div><div>{activeMovement.created_by_name || '—'}</div></div>
                </div>

                {(activeMovement.reference || activeMovement.note) && (
                  <div style={{ marginBottom: 16, fontSize: 13 }}>
                    {activeMovement.reference && (
                      <div style={{ marginBottom: 8 }}>
                        <strong>Source:</strong>{' '}
                        {(activeMovement.linked_po_id || activeMovement.linked_order_id || isPoReference(activeMovement.reference)) ? (
                          <button onClick={() => navigateToSource(activeMovement)} style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 13 }}>
                            {activeMovement.reference}
                          </button>
                        ) : activeMovement.reference}
                      </div>
                    )}
                    {activeMovement.note && <div><strong>Note:</strong> {activeMovement.note}</div>}
                  </div>
                )}

                {activeMovement.reversed_by_id && (
                  <div style={{ marginBottom: 16, padding: '10px 12px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, fontSize: 13, color: '#5b21b6' }}>
                    This movement was reversed (reversal #{activeMovement.reversed_by_movement_id || activeMovement.reversed_by_id}).
                  </div>
                )}
                {activeMovement.reversal_of_id && (
                  <div style={{ marginBottom: 16, padding: '10px 12px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, fontSize: 13, color: '#5b21b6' }}>
                    This is a reversal of movement #{activeMovement.reversal_of_id}.
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button onClick={() => { setShowDetail(false); setActiveMovement(null); }}
                    style={{ padding: '10px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Close</button>
                  {activeMovement.can_reverse && (
                    <button onClick={submitReverse} disabled={saving}
                      style={{ padding: '10px 20px', background: '#9333ea', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
                      {saving ? 'Reversing...' : '↩️ Reverse Movement'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Manual Movement Modal */}
      {showModal && (
        <div style={s.overlay} onClick={() => setShowModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>Manual Stock Movement</h2>

            {/* Product */}
            <div style={s.formGroup}>
              <label style={s.label}>Product *</label>
              <select value={form.product_id} onChange={e => handleProductChange(e.target.value)} style={s.input}>
                <option value="">-- Select Product --</option>
                {products.map((p: any) => <option key={p.id} value={p.id}>{p.name} (Stock: {p.stock ?? 0})</option>)}
              </select>
              {form.product_id && (() => {
                const p = products.find((pr: any) => String(pr.id) === form.product_id);
                if (!p) return null;
                const autoSupplier = form.supplier_id ? suppliers.find((s: any) => String(s.id) === form.supplier_id) : null;
                return (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: '#f0f9ff', borderRadius: 8, fontSize: 12, color: '#1e40af', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <span>📦 Stock: <strong>{p.stock ?? 0}</strong></span>
                    {p.cost ? <span>💰 Cost: <strong>£{parseFloat(p.cost).toFixed(2)}</strong></span> : null}
                    {p.unit ? <span>📐 Unit: <strong>{p.unit}</strong></span> : null}
                    {autoSupplier ? <span>🏭 Supplier: <strong>{autoSupplier.name}</strong></span> : null}
                  </div>
                );
              })()}
            </div>

            {/* Type — 3 pill buttons */}
            <div style={s.formGroup}>
              <label style={s.label}>Type *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {([
                  { value: 'stock_in',   label: '➕ Stock In',    desc: 'Stock arrived or customer returned', color: '#10b981' },
                  { value: 'stock_out',  label: '➖ Stock Out',   desc: 'Damaged, lost, or sent back to supplier', color: '#ef4444' },
                  { value: 'adjustment', label: '🔧 Adjustment',  desc: 'Fix a counting mistake', color: '#f59e0b' },
                ] as { value: string; label: string; desc: string; color: string }[]).map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setForm(f => ({ ...f, type: opt.value, quantity: '', unit_cost: '' }))}
                    style={{
                      flex: 1, padding: '10px 8px',
                      border: `2px solid ${form.type === opt.value ? opt.color : '#e5e7eb'}`,
                      borderRadius: 8,
                      background: form.type === opt.value ? `${opt.color}18` : '#fafafa',
                      cursor: 'pointer', textAlign: 'center' as const,
                    }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: form.type === opt.value ? opt.color : '#374151' }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div style={s.formGroup}>
              <label style={s.label}>Quantity *</label>
              {form.type === 'stock_out' ? (
                <>
                  <input type="number" step="1" min="1" value={form.quantity}
                    onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} style={s.input}
                    placeholder="Units to remove (positive number)" />
                  <p style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>⚠️ Removes stock. Enter a positive number — e.g. 5 deducts 5 units.</p>
                </>
              ) : form.type === 'adjustment' ? (
                <>
                  <input type="number" step="1" value={form.quantity}
                    onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} style={s.input}
                    placeholder="+ to add stock, − to remove" />
                  <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Use negative (e.g. −5) to remove, positive to add. No accounting entry created.</p>
                </>
              ) : (
                <input type="number" step="1" min="1" value={form.quantity}
                  onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} style={s.input}
                  placeholder="Units to add" />
              )}
            </div>

            {/* Unit cost — shown for stock_in and stock_out only */}
            {(form.type === 'stock_in' || form.type === 'stock_out') && (
              <div style={s.formGroup}>
                <label style={s.label}>Unit Cost (£) <span style={{ fontWeight: 400, color: '#6b7280' }}>— optional, for accounting entry</span></label>
                <input type="number" step="0.01" min="0" value={form.unit_cost}
                  onChange={e => setForm(f => ({ ...f, unit_cost: e.target.value }))} style={s.input}
                  placeholder="Leave blank to use product's stored cost" />
                {form.unit_cost && parseFloat(form.unit_cost) > 0 && form.quantity && parseFloat(form.quantity) > 0 && (
                  <p style={{ fontSize: 12, color: form.type === 'stock_in' ? '#10b981' : '#ef4444', marginTop: 4 }}>
                    {form.type === 'stock_in'
                      ? `📦 Inventory value impact: +£${(parseFloat(form.quantity) * parseFloat(form.unit_cost)).toFixed(2)}`
                      : `📦 Inventory value impact: -£${(parseFloat(form.quantity) * parseFloat(form.unit_cost)).toFixed(2)}`}
                  </p>
                )}
              </div>
            )}

            {/* Reason — select first; determines whether supplier dropdown appears */}
            <div style={s.formGroup}>
              <label style={s.label}>
                Reason
                <span style={{ fontWeight: 400, color: '#6b7280' }}>
                  {form.type === 'stock_out' ? ' — required to determine supplier linking' : ' — optional'}
                </span>
              </label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {(form.type === 'stock_in'
                  ? ['Supplier delivery', 'Customer return', 'Stock found', 'Opening balance']
                  : form.type === 'stock_out'
                    ? ['Damaged goods', 'Expired stock', 'Supplier return', 'Theft/loss', 'Internal use']
                    : ['Stock count correction', 'Barcode mismatch', 'System correction', 'Manual fix']
                ).map((r: string) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setForm(f => ({
                      ...f,
                      reason: r,
                      // Clear supplier if stock_out reason is NOT a supplier return
                      supplier_id: (f.type === 'stock_out' && r !== 'Supplier return') ? '' : f.supplier_id,
                    }))}
                    style={{
                      padding: '4px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
                      border: `1px solid ${form.reason === r ? '#10b981' : '#d1d5db'}`,
                      background: form.reason === r ? '#d1fae5' : '#f9fafb',
                      color: form.reason === r ? '#065f46' : '#374151',
                      fontWeight: form.reason === r ? 600 : 400,
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} style={s.input}
                placeholder="e.g. damaged in transit, customer returned, supplier return, internal use..." />
            </div>

            {/* Supplier — Stock In: always optional. Stock Out: only appears for "Supplier return" */}
            {(form.type === 'stock_in' || (form.type === 'stock_out' && form.reason === 'Supplier return')) && (
              <div style={s.formGroup}>
                <label style={s.label}>
                  {form.type === 'stock_in' ? '🏭 Supplier (who delivered)' : '🏭 Supplier (returning goods to)'}
                  <span style={{ fontWeight: 400, color: form.type === 'stock_out' ? '#dc2626' : '#6b7280' }}>
                    {form.type === 'stock_out' ? ' — will appear in Supplier View' : ' — optional'}
                  </span>
                </label>
                <select value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))} style={s.input}>
                  <option value="">No specific supplier / internal</option>
                  {suppliers.map((sup: any) => <option key={sup.id} value={String(sup.id)}>{sup.name}</option>)}
                </select>
                {form.type === 'stock_out' && (
                  <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    ℹ️ Only supplier returns with a supplier selected appear in Supplier View. Damaged, expired, and internal stock outs remain internal.
                  </p>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)}
                style={{ padding: '10px 24px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button onClick={submitMovement} disabled={saving}
                style={{ padding: '10px 24px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
                {saving ? 'Saving...' : '✅ Save Movement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>📥 Import Stock Movements from CSV</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>
              Expected columns: <code>product_name, type, quantity, unit_cost, reason, supplier_name</code>
            </p>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
              Valid types: <code>in, out, adjustment, return, waste, transfer</code>. Only <code>product_name, type, quantity</code> are required.
            </p>

            {importErrors.length > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <strong style={{ color: '#dc2626' }}>Errors ({importErrors.length}):</strong>
                <ul style={{ margin: '6px 0 0 0', paddingLeft: 20 }}>
                  {importErrors.map((e, i) => (
                    <li key={i} style={{ fontSize: 13, color: '#b91c1c' }}>
                      {e.row > 0 ? `Row ${e.row}: ` : ''}{e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ overflowX: 'auto', marginBottom: 20 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['#', 'Product Name', 'Type', 'Qty', 'Unit Cost', 'Reason', 'Supplier'].map(h => (
                      <th key={h} style={{ ...s.th, fontSize: 12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvRows.slice(0, 50).map((r, i) => (
                    <tr key={i}>
                      <td style={s.td}>{i + 1}</td>
                      <td style={s.td}>{r.product_name}</td>
                      <td style={s.td}>{r.type}</td>
                      <td style={s.td}>{r.quantity}</td>
                      <td style={s.td}>{r.unit_cost || '—'}</td>
                      <td style={s.td}>{r.reason || '—'}</td>
                      <td style={s.td}>{r.supplier_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {csvRows.length > 50 && <p style={{ fontSize: 12, color: '#6b7280' }}>Showing first 50 of {csvRows.length} rows.</p>}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowImportModal(false); setCsvRows([]); setImportErrors([]); }}
                style={{ padding: '10px 24px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
                Cancel
              </button>
              <button onClick={submitCsvImport} disabled={importing || csvRows.length === 0}
                style={{ padding: '10px 24px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: importing ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
                {importing ? 'Importing...' : `✅ Import ${csvRows.length} Row${csvRows.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default StockMovements;
